import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Layout from '../components/Layout';
import AutoComplete from '../components/AutoComplete';
import DataTable from '../components/DataTable';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import {
  guiaRemisionService, almacenService, sucursalService,
  entidadService, trabajadorService, tiposMovimientoService,
  serieService, tablaService, articuloService, unidadMedidaService,
  datosGeneralesService,
} from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/requerimientos.css';
import '../styles/ordenes_compra.css';
import '../styles/datatable.css';


const today = () => new Date().toISOString().split('T')[0];
const weekAgo = () => { const d = new Date(); d.setDate(d.getDate() - 6); return d.toISOString().split('T')[0]; };
const fmtNumGR = (serie, numero) => `${(serie || '').trim()}-${String(numero || 0).padStart(6, '0')}`;

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const PrintIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);


const ESTADO_GR_CFG = {
  'GENERADO': { color: '#22c55e', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.3)' },
  'ANULADO':  { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.25)' },
};

const Badge = ({ label, cfg }) => {
  const c = cfg || { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.2)' };
  return (
    <span style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 10, padding: '2px 9px', fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
};

const inputStyle = {
  height: 30, padding: '0 9px', fontSize: 12, borderRadius: 7, boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
  color: '#e2e8f0', outline: 'none', fontFamily: 'Inter, sans-serif', width: '100%',
};

const selectStyle = {
  height: 30, padding: '0 8px', fontSize: 12, borderRadius: 7, boxSizing: 'border-box',
  background: 'rgba(15,27,45,0.9)', border: '1px solid rgba(255,255,255,0.11)',
  color: '#e2e8f0', outline: 'none', fontFamily: 'Inter, sans-serif', width: '100%', cursor: 'pointer',
};

const INIT_FORM = {
  id_almacen: '', id_tipo_movimiento: '', id_serie: '',
  tipo_guia: 'REMITENTE',
  fecha_guia: today(),
  id_proveedor: '', ruc_proveedor: '', proveedor_nombre: '',
  id_tipo_doc_ref1: '', num_doc_ref1: '',
  id_tipo_doc_ref2: '', num_doc_ref2: '',
  id_almacen_destino: '',
  fecha_traslado: today(), fecha_entrega: today(),
  id_solicitante: '',
  id_motivo_traslado: '',
  observacion: '',
};

export default function GuiasRemision() {
  const { user, sucursal: sesionSucursal } = useAuth();
  const [view, setView] = useState('list'); // 'list' | 'form'

  /* ── Lista ── */
  const [records,     setRecords]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [alert,       setAlert]       = useState(null);
  const [toast,       setToast]       = useState(null);
  const [confirmDel,  setConfirmDel]  = useState(null);

  /* ── Filtros lista ── */
  const [fFechaIni, setFFechaIni] = useState(weekAgo);
  const [fFechaFin, setFFechaFin] = useState(today);
  const [fNumero,   setFNumero]   = useState('');
  const [fEstado,   setFEstado]   = useState('');
  const [fTipo,     setFTipo]     = useState('');

  /* ── Catálogos ── */
  const [almacenes,       setAlmacenes]       = useState([]);
  const [tiposMovimiento, setTiposMovimiento] = useState([]);
  const [series,          setSeries]          = useState([]);
  const [entidades,       setEntidades]       = useState([]);
  const [trabajadores,    setTrabajadores]    = useState([]);
  const [tiposDoc,        setTiposDoc]        = useState([]);
  const [motivoTraslado,  setMotivoTraslado]  = useState([]);
  const [articulos,       setArticulos]       = useState([]);
  const [unidades,        setUnidades]        = useState([]);
  const [loadingCat,      setLoadingCat]      = useState(false);
  const catLoaded = useRef(false);

  /* ── Form nuevo ── */
  const [form,    setForm]    = useState(INIT_FORM);
  const [detalle, setDetalle] = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [creado,  setCreado]  = useState(null);

  /* ── Modal edición cabecera ── */
  const [editModal,      setEditModal]      = useState(null); // guía seleccionada
  const [editForm,       setEditForm]       = useState({});
  const [savingEdit,     setSavingEdit]     = useState(false);

  const tdSvc = useRef(tablaService('tipos-documento'));
  const mtSvc = useRef(tablaService('motivos-traslado'));

  /* ── Cargar lista ── */
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const r = await guiaRemisionService.getAll();
      setRecords(r.data || []);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar guías de remisión.') });
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── Cargar catálogos ── */
  const fetchCatalogos = useCallback(async () => {
    if (catLoaded.current) return;
    setLoadingCat(true);
    try {
      const [alm, tm, ser, ent, trab, td, mt, ums] = await Promise.all([
        almacenService.getAll(),
        tiposMovimientoService.getAll(),
        serieService.getAll('GS'),
        entidadService.getAll(),
        trabajadorService.getAll(),
        tdSvc.current.getAll(),
        mtSvc.current.getAll(),
        unidadMedidaService.getAll(),
      ]);
      setAlmacenes(alm.data || []);
      setTiposMovimiento((tm.data || []).filter(t => t.tipo === 'SALIDA'));
      setSeries(ser.data || []);
      setEntidades(ent.data || []);
      setTrabajadores(trab.data || []);
      setTiposDoc(td.data || []);
      setMotivoTraslado(mt.data || []);
      // Artículos: búsqueda remota, no se precargan.
      setArticulos([]);
      setUnidades(ums.data || []);
      catLoaded.current = true;
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar catálogos.') });
    } finally {
      setLoadingCat(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  /* ── Mapas lookup ── */
  const almacenesSucursal = useMemo(
    () => sesionSucursal?.id_sucursal
      ? almacenes.filter(a => String(a.id_sucursal) === String(sesionSucursal.id_sucursal))
      : almacenes,
    [almacenes, sesionSucursal]
  );

  const articuloMap = useMemo(
    () => Object.fromEntries(articulos.map(a => [String(a.id_articulo), a])),
    [articulos]
  );
  const unidadMap = useMemo(
    () => Object.fromEntries(unidades.map(u => [String(u.id_unidad), u.codigo])),
    [unidades]
  );

  /* ── Filtrado lista ── */
  const recordsFiltrados = useMemo(() => {
    return records.filter(r => {
      const fecha = (r.fecha_guia || '').slice(0, 10);
      if (fFechaIni && fecha < fFechaIni) return false;
      if (fFechaFin && fecha > fFechaFin) return false;
      if (fNumero && !String(r.numero).includes(fNumero)) return false;
      if (fEstado && r.estado !== fEstado) return false;
      if (fTipo && r.tipo_guia !== fTipo) return false;
      return true;
    });
  }, [records, fFechaIni, fFechaFin, fNumero, fEstado, fTipo]);

  /* ── Helpers form ── */
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const n = v => (v !== '' && v !== null && v !== undefined) ? Number(v) : null;

  /* ── Detalle ── */
  const addLinea    = () => setDetalle(p => [...p, {
    _key: Date.now(), id_articulo: '', id_unidad: '', descripcion: '', cantidad: 1, observacion: '',
    articulo_codigo: '', articulo_descripcion: '', unidad_descripcion: '',
  }]);
  const removeLinea = k  => setDetalle(p => p.filter(l => l._key !== k));
  const updateLinea = (k, field, val) => setDetalle(p => p.map(l => l._key === k ? { ...l, [field]: val } : l));

  const onSelectArticulo = (key, idArticulo, art) => {
    setDetalle(p => p.map(l =>
      l._key === key
        ? {
            ...l,
            id_articulo: idArticulo || '',
            id_unidad: art?.id_unidad ? String(art.id_unidad) : '',
            descripcion: art?.descripcion || '',
            articulo_codigo: art?.codigo_interno || '',
            articulo_descripcion: art?.descripcion || '',
            unidad_descripcion: art?.unidad_descripcion || '',
          }
        : l
    ));
  };

  /* ── Ir a formulario ── */
  const irAFormulario = () => {
    fetchCatalogos();
    setForm(INIT_FORM);
    setDetalle([]);
    setCreado(null);
    setView('form');
  };

  /* ── Guardar nuevo ── */
  const handleSubmit = async () => {
    if (!form.id_almacen)   { setAlert({ type: 'warning', message: 'Seleccione un almacén.' }); return; }
    if (!form.tipo_guia)    { setAlert({ type: 'warning', message: 'Seleccione el tipo de guía.' }); return; }
    if (!form.fecha_guia)   { setAlert({ type: 'warning', message: 'Ingrese la fecha de guía.' }); return; }
    if (form.fecha_entrega && form.fecha_guia && form.fecha_entrega < form.fecha_guia) {
      setAlert({ type: 'warning', message: 'La fecha de entrega no puede ser menor a la fecha de la guía.' }); return;
    }
    if (form.fecha_entrega && form.fecha_traslado && form.fecha_entrega < form.fecha_traslado) {
      setAlert({ type: 'warning', message: 'La fecha de entrega no puede ser menor a la fecha de traslado.' }); return;
    }
    if (detalle.length === 0) { setAlert({ type: 'warning', message: 'Agregue al menos un artículo.' }); return; }
    for (const l of detalle) {
      if (!l.id_articulo) { setAlert({ type: 'warning', message: 'Todos los ítems deben tener un artículo.' }); return; }
      if (!l.cantidad || parseFloat(l.cantidad) <= 0) { setAlert({ type: 'warning', message: 'La cantidad de cada ítem debe ser mayor a 0.' }); return; }
    }

    const payload = {
      id_almacen:         n(form.id_almacen),
      id_sucursal:        user?.id_sucursal ? n(user.id_sucursal) : null,
      id_tipo_movimiento: n(form.id_tipo_movimiento),
      id_serie:           n(form.id_serie),
      tipo_guia:          form.tipo_guia,
      fecha_guia:         form.fecha_guia,
      id_proveedor:       n(form.id_proveedor),
      ruc_proveedor:      form.ruc_proveedor || null,
      id_tipo_doc_ref1:   n(form.id_tipo_doc_ref1),
      num_doc_ref1:       form.num_doc_ref1 || null,
      id_tipo_doc_ref2:   n(form.id_tipo_doc_ref2),
      num_doc_ref2:       form.num_doc_ref2 || null,
      id_almacen_destino: n(form.id_almacen_destino),
      fecha_traslado:     form.fecha_traslado || null,
      fecha_entrega:      form.fecha_entrega  || null,
      id_solicitante:     n(form.id_solicitante),
      id_motivo_traslado: n(form.id_motivo_traslado),
      observacion:        form.observacion || null,
      detalle: detalle.map((l, i) => ({
        orden:       i + 1,
        id_articulo: n(l.id_articulo),
        id_unidad:   n(l.id_unidad),
        cantidad:    parseFloat(l.cantidad) || 1,
        observacion: l.observacion || null,
      })),
    };

    setSaving(true);
    try {
      const res = await guiaRemisionService.create(payload);
      setCreado(res.data);
      fetchRecords();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar la guía de remisión.') });
    } finally {
      setSaving(false);
    }
  };

  /* ── Eliminar ── */
  const handleDelete = (rec) => {
    setConfirmDel(rec);
  };
  const confirmDelete = async () => {
    if (!confirmDel) return;
    try {
      await guiaRemisionService.delete(confirmDel.id_guia);
      setToast({ type: 'success', message: `Guía ${fmtNumGR(confirmDel.serie_codigo, confirmDel.numero)} eliminada.` });
      setRecords(prev => prev.filter(r => r.id_guia !== confirmDel.id_guia));
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al eliminar la guía.') });
    } finally {
      setConfirmDel(null);
    }
  };

  /* ── Impresión ── */
  const handlePrint = async (rec) => {
    try {
      const [detRes, empRes] = await Promise.allSettled([
        guiaRemisionService.getById(rec.id_guia),
        datosGeneralesService.get(),
      ]);
      const doc = detRes.status === 'fulfilled' ? detRes.value.data : rec;
      const emp = empRes.status === 'fulfilled' ? empRes.value.data : {};
      const det = doc.detalle || [];
      const numGR = fmtNumGR(doc.serie_codigo, doc.numero);
      const fmt = (d) => d ? String(d).slice(0, 10).split('-').reverse().join('/') : '—';


      const html = `<!DOCTYPE html><html lang="es"><head>
<meta charset="UTF-8"/>
<title>Guía de Remisión ${numGR}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
body{font-family:'Segoe UI',Arial,sans-serif;font-size:10.5px;color:#1e293b;background:#fff;padding:22px 30px 24px}
.top-stripe{height:5px;background:linear-gradient(90deg,#1a3a6b 0%,#2563eb 100%);border-radius:2px;margin-bottom:16px}
.header{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px}
.header-left{display:flex;align-items:center;gap:14px}
.logo-box{width:52px;height:52px;background:#1a3a6b;border-radius:8px;display:flex;align-items:center;justify-content:center}
.logo-img{max-height:56px;max-width:130px;object-fit:contain}
.emp-name{font-size:14px;font-weight:700;color:#1a3a6b;line-height:1.2}
.emp-ruc{font-size:10px;font-weight:600;color:#475569;margin-top:2px}
.emp-sub{font-size:9px;color:#64748b;margin-top:2px;line-height:1.5}
.doc-box{border:2.5px solid #1a3a6b;border-radius:8px;padding:9px 18px;text-align:center;min-width:170px;background:#f5f8ff}
.doc-box-label{font-size:7.5px;font-weight:700;color:#1a3a6b;text-transform:uppercase;letter-spacing:1.2px}
.doc-box-title{font-size:13px;font-weight:800;color:#1a3a6b;margin:3px 0 2px}
.doc-box-num{font-size:18px;font-weight:700;color:#1a3a6b;font-family:monospace;letter-spacing:1px}
.doc-box-sub{font-size:9px;color:#475569;margin-top:3px}
.divider{border:none;border-top:2.5px solid #1a3a6b;margin:10px 0 12px}
.sec-header{background:#1a3a6b;color:#fff;font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;padding:4px 10px;margin:10px 0 7px;border-radius:3px}
.grid-4{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:5px 10px;margin-bottom:5px}
.grid-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px 10px;margin-bottom:5px}
.grid-2{display:grid;grid-template-columns:1fr 1fr;gap:5px 10px;margin-bottom:5px}
.field{border:1px solid #c8dcf5;border-radius:3px;padding:4px 7px;background:#f5f8ff}
.field-label{font-size:7.5px;font-weight:700;color:#7a9cc4;text-transform:uppercase;letter-spacing:0.8px}
.field-value{font-size:10.5px;color:#1e293b;font-weight:500;min-height:13px;margin-top:1px}
.badge{display:inline-block;padding:2px 9px;border-radius:3px;font-size:9px;font-weight:700;border:1px solid}
table{width:100%;border-collapse:collapse;margin-top:2px}
thead tr{background:#1a3a6b}
th{font-size:8.5px;font-weight:700;color:#fff;text-transform:uppercase;letter-spacing:0.5px;padding:6px 8px;text-align:left;border:1px solid #1a3a6b}
th.r{text-align:right}th.c{text-align:center}
td{padding:5px 8px;border:1px solid #dce8f8;font-size:10px;vertical-align:middle}
td.r{text-align:right}td.c{text-align:center}
tr:nth-child(even) td{background:#f5f8ff}
tr:nth-child(odd) td{background:#fff}
tfoot td{background:#e8f0fb!important;font-weight:700;font-size:9.5px;color:#1a3a6b;border-top:2px solid #a8c4e8}
.obs-box{border:1px solid #c8dcf5;border-radius:4px;padding:6px 10px;background:#f5f8ff;margin-top:6px;font-size:10px;color:#334155;min-height:28px}
.page-footer{margin-top:18px;padding-top:7px;border-top:1px solid #dce8f8;display:flex;justify-content:space-between;font-size:8px;color:#94a3b8}
@media print{
  body{padding:8px 14px 14px}
  @page{margin:8mm 10mm;size:A4}
  .top-stripe{background:linear-gradient(90deg,#1a3a6b 0%,#2563eb 100%)!important}
  .sec-header{background:#1a3a6b!important;color:#fff!important}
  thead tr{background:#1a3a6b!important}
  th{color:#fff!important;background:#1a3a6b!important}
  .field,.obs-box{background:#f5f8ff!important}
  tfoot td{background:#e8f0fb!important}
}
</style></head><body>

<div class="top-stripe"></div>
<div class="header">
  <div class="header-left">
    ${emp.logo_base64
      ? `<img src="${emp.logo_base64}" class="logo-img" alt="Logo"/>`
      : `<div class="logo-box"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg></div>`}
    <div>
      <div class="emp-name">${emp.razon_social || 'Empresa'}</div>
      <div class="emp-ruc">RUC: ${emp.ruc_empresa || '—'}</div>
      <div class="emp-sub">${[emp.direccion, emp.distrito, emp.departamento].filter(Boolean).join(' — ')}</div>
    </div>
  </div>
  <div class="doc-box">
    <div class="doc-box-label">Documento</div>
    <div class="doc-box-title">GUÍA DE REMISIÓN</div>
    <div class="doc-box-num">${numGR}</div>
    <div class="doc-box-sub">${doc.tipo_guia || 'REMITENTE'}</div>
  </div>
</div>
<hr class="divider"/>

<div class="sec-header">Identificación</div>
<div class="grid-4">
  <div class="field"><div class="field-label">Fecha de Guía</div><div class="field-value">${fmt(doc.fecha_guia)}</div></div>
  <div class="field"><div class="field-label">Fecha de Traslado</div><div class="field-value">${fmt(doc.fecha_traslado)}</div></div>
  <div class="field"><div class="field-label">Fecha de Entrega</div><div class="field-value">${fmt(doc.fecha_entrega)}</div></div>
  <div class="field"><div class="field-label">Estado</div><div class="field-value">
    <span class="badge" style="background:${doc.estado==='GENERADO'?'rgba(34,197,94,0.12)':'rgba(148,163,184,0.1)'};color:${doc.estado==='GENERADO'?'#16a34a':'#64748b'};border-color:${doc.estado==='GENERADO'?'rgba(34,197,94,0.4)':'rgba(148,163,184,0.3)'}">${doc.estado||'—'}</span>
  </div></div>
</div>
<div class="grid-3">
  <div class="field"><div class="field-label">Sucursal</div><div class="field-value">${doc.sucursal_nombre||'—'}</div></div>
  <div class="field"><div class="field-label">Almacén Origen</div><div class="field-value">${doc.almacen_nombre||'—'}</div></div>
  <div class="field"><div class="field-label">Tipo de Movimiento</div><div class="field-value">${doc.tipo_movimiento_descripcion||'—'}</div></div>
</div>

<div class="sec-header">Datos de Traslado</div>
<div class="grid-3">
  <div class="field"><div class="field-label">Almacén Destino</div><div class="field-value">${doc.almacen_destino_nombre||'—'}</div></div>
  <div class="field"><div class="field-label">Dirección Destino</div><div class="field-value">${doc.almacen_destino_direccion||'—'}</div></div>
  <div class="field"><div class="field-label">Motivo de Traslado</div><div class="field-value">${doc.motivo_traslado_codigo?doc.motivo_traslado_codigo+' - ':''} ${doc.motivo_traslado_descripcion||'—'}</div></div>
</div>
<div class="grid-2">
  <div class="field"><div class="field-label">Solicitante</div><div class="field-value">${doc.solicitante_nombre||'—'}</div></div>
  <div class="field"><div class="field-label">Proveedor / Cliente</div><div class="field-value">${doc.ruc_proveedor?doc.ruc_proveedor+' — ':''} ${doc.proveedor_nombre||'—'}</div></div>
</div>

${(doc.id_tipo_doc_ref1 || doc.id_tipo_doc_ref2) ? `
<div class="sec-header">Referencias Documentales</div>
<div class="grid-4">
  ${doc.tipo_doc_ref1_descripcion ? `<div class="field"><div class="field-label">Tipo Doc. Ref. 1</div><div class="field-value">${doc.tipo_doc_ref1_descripcion}</div></div>` : ''}
  ${doc.num_doc_ref1 ? `<div class="field"><div class="field-label">Nº Ref. 1</div><div class="field-value">${doc.num_doc_ref1}</div></div>` : ''}
  ${doc.tipo_doc_ref2_descripcion ? `<div class="field"><div class="field-label">Tipo Doc. Ref. 2</div><div class="field-value">${doc.tipo_doc_ref2_descripcion}</div></div>` : ''}
  ${doc.num_doc_ref2 ? `<div class="field"><div class="field-label">Nº Ref. 2</div><div class="field-value">${doc.num_doc_ref2}</div></div>` : ''}
</div>` : ''}

<div class="sec-header">Artículos</div>
<table>
  <thead>
    <tr>
      <th style="width:28px" class="c">#</th>
      <th style="width:90px">Código</th>
      <th>Descripción del Artículo</th>
      <th style="width:60px" class="c">U.M.</th>
      <th style="width:80px" class="r">Cantidad</th>
      <th style="width:120px">Observación</th>
    </tr>
  </thead>
  <tbody>
    ${det.map((d, i) => `<tr>
      <td class="c" style="color:#94a3b8">${i + 1}</td>
      <td style="font-family:monospace;font-size:9.5px">${d.articulo_codigo || '—'}</td>
      <td style="font-weight:500">${d.articulo_descripcion || '—'}</td>
      <td class="c" style="color:#64748b">${d.unidad_codigo || '—'}</td>
      <td class="r" style="font-weight:600">${parseFloat(d.cantidad||0).toLocaleString('es-PE',{minimumFractionDigits:2,maximumFractionDigits:4})}</td>
      <td style="color:#64748b;font-size:9.5px">${d.observacion || ''}</td>
    </tr>`).join('')}
  </tbody>
  <tfoot>
    <tr>
      <td colspan="4" style="text-align:right;padding-right:12px">Total ítems:</td>
      <td class="r">${det.length}</td>
      <td></td>
    </tr>
  </tfoot>
</table>

${doc.observacion ? `
<div class="field" style="margin-top:8px">
  <div class="field-label">Observaciones</div>
  <div class="obs-box">${doc.observacion}</div>
</div>` : ''}


<div class="page-footer">
  <span>${emp.razon_social || ''} ${emp.ruc_empresa ? '— RUC: ' + emp.ruc_empresa : ''}</span>
  <span>Impreso: ${new Date().toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
</div>

</body></html>`;

      const w = window.open('', '_blank', 'width=950,height=780');
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 450);
    } catch {
      setAlert({ type: 'error', message: 'Error al generar la impresión.' });
    }
  };

  /* ── Abrir modal edición ── */
  const abrirEditar = (rec) => {
    fetchCatalogos();
    setEditModal(rec);
    setEditForm({
      tipo_guia:          rec.tipo_guia || 'REMITENTE',
      fecha_guia:         (rec.fecha_guia || '').slice(0, 10),
      id_tipo_movimiento: rec.id_tipo_movimiento ? String(rec.id_tipo_movimiento) : '',
      id_proveedor:       rec.id_proveedor ? String(rec.id_proveedor) : '',
      ruc_proveedor:      rec.ruc_proveedor || '',
      id_tipo_doc_ref1:   rec.id_tipo_doc_ref1 ? String(rec.id_tipo_doc_ref1) : '',
      num_doc_ref1:       rec.num_doc_ref1 || '',
      id_tipo_doc_ref2:   rec.id_tipo_doc_ref2 ? String(rec.id_tipo_doc_ref2) : '',
      num_doc_ref2:       rec.num_doc_ref2 || '',
      id_almacen_destino: rec.id_almacen_destino ? String(rec.id_almacen_destino) : '',
      fecha_traslado:     (rec.fecha_traslado || '').slice(0, 10),
      fecha_entrega:      (rec.fecha_entrega  || '').slice(0, 10),
      id_solicitante:     rec.id_solicitante ? String(rec.id_solicitante) : '',
      id_motivo_traslado: rec.id_motivo_traslado ? String(rec.id_motivo_traslado) : '',
      observacion:        rec.observacion || '',
    });
  };

  const setEF = (k, v) => setEditForm(f => ({ ...f, [k]: v }));

  const handleSaveEdit = async () => {
    if (!editForm.fecha_guia) { setAlert({ type: 'warning', message: 'Ingrese la fecha de guía.' }); return; }
    setSavingEdit(true);
    try {
      await guiaRemisionService.update(editModal.id_guia, {
        tipo_guia:          editForm.tipo_guia,
        fecha_guia:         editForm.fecha_guia,
        id_tipo_movimiento: n(editForm.id_tipo_movimiento),
        id_proveedor:       n(editForm.id_proveedor),
        ruc_proveedor:      editForm.ruc_proveedor || null,
        id_tipo_doc_ref1:   n(editForm.id_tipo_doc_ref1),
        num_doc_ref1:       editForm.num_doc_ref1 || null,
        id_tipo_doc_ref2:   n(editForm.id_tipo_doc_ref2),
        num_doc_ref2:       editForm.num_doc_ref2 || null,
        id_almacen_destino: n(editForm.id_almacen_destino),
        fecha_traslado:     editForm.fecha_traslado || null,
        fecha_entrega:      editForm.fecha_entrega  || null,
        id_solicitante:     n(editForm.id_solicitante),
        id_motivo_traslado: n(editForm.id_motivo_traslado),
        observacion:        editForm.observacion || null,
      });
      setToast({ type: 'success', message: 'Guía actualizada correctamente.' });
      setEditModal(null);
      fetchRecords();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al actualizar la guía.') });
    } finally {
      setSavingEdit(false);
    }
  };

  /* ════════════════════════════════════════════════════════════════
     PANTALLA DE ÉXITO
  ════════════════════════════════════════════════════════════════ */
  if (creado) {
    const ec = ESTADO_GR_CFG[creado.estado] || {};
    return (
      <Layout>
        <div style={{ maxWidth: 520, margin: '80px auto', textAlign: 'center', padding: '0 20px' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(34,197,94,0.1)', border: '2px solid rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600, marginBottom: 8 }}>
            Guía de Remisión generada
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#60a5fa', letterSpacing: 2, marginBottom: 6 }}>
            {fmtNumGR(creado.serie_codigo, creado.numero)}
          </div>
          <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 20 }}>
            {creado.almacen_nombre} · {(creado.fecha_guia || '').slice(0, 10)}
          </div>
          <Badge label={creado.estado || 'GENERADO'} cfg={ec} />
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 36 }}>
            <button className="btn btn-secondary" onClick={() => setView('list')}>
              Ver lista
            </button>
            <button className="btn btn-primary" onClick={irAFormulario}>
              + Nueva Guía
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  /* ════════════════════════════════════════════════════════════════
     VISTA FORMULARIO
  ════════════════════════════════════════════════════════════════ */
  if (view === 'form') {
    const almacenesDestino = almacenes.map(a => ({
      ...a,
      display_destino: `${a.nombre}${a.direccion ? ' | ' + a.direccion : ''}${a.ubigeo ? ' [' + a.ubigeo + ']' : ''}`,
    }));

    return (
      <Layout>
        {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

        <div className="rq-page">
          {/* ── Header ── */}
          <div className="rq-header">
            <div className="rq-header-left">
              <button className="oc-back-btn" onClick={() => setView('list')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <div className="rq-header-title">
                <div className="rq-header-meta">Almacén — Guías de Remisión</div>
                <div className="rq-header-name">Nueva Guía de Remisión</div>
              </div>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="rq-body" style={{ overflowY: 'auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

              {/* ── SECCIÓN 1: Identificación ── */}
              <div className="rq-card">
                <div className="rq-card-title">Identificación</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 80px 1fr', gap: '0 14px', alignItems: 'start' }}>
                  {/* Sucursal — solo lectura */}
                  <div className="form-row">
                    <label>Sucursal</label>
                    <div style={{
                      padding: '7px 12px', borderRadius: 8, fontSize: 13,
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                      color: '#94a3b8', minHeight: 34, display: 'flex', alignItems: 'center',
                    }}>
                      {sesionSucursal?.nombre || '—'}
                    </div>
                  </div>
                  <AutoComplete
                    label="Almacén *"
                    value={String(form.id_almacen || '')}
                    options={almacenesSucursal} idField="id_almacen" displayField="nombre"
                    onChange={v => setF('id_almacen', v)}
                    placeholder="Seleccionar almacén..."
                  />
                  <AutoComplete
                    label="Tipo de Movimiento"
                    value={String(form.id_tipo_movimiento || '')}
                    options={tiposMovimiento} idField="id_tipo_movimiento"
                    displayField={t => `${t.codigo} - ${t.descripcion}`}
                    onChange={v => setF('id_tipo_movimiento', v)}
                    placeholder="Seleccionar tipo (SALIDA)..."
                  />
                  {/* Tipo de Documento — solo lectura */}
                  <div className="form-row">
                    <label>Tipo Doc.</label>
                    <div style={{
                      padding: '7px 10px', borderRadius: 8, fontSize: 13,
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                      color: '#94a3b8', minHeight: 34, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontWeight: 700, letterSpacing: 1,
                    }}>
                      GS
                    </div>
                  </div>
                  <AutoComplete
                    label="Serie (GR)"
                    value={String(form.id_serie || '')}
                    options={series} idField="id_serie"
                    displayField={s => `${s.serie} - ${s.descripcion || ''}`}
                    onChange={v => setF('id_serie', v)}
                    placeholder="Seleccionar serie..."
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '0 14px', alignItems: 'start', marginTop: 4 }}>
                  <div className="form-row">
                    <label>Tipo de Guía *</label>
                    <select value={form.tipo_guia} onChange={e => setF('tipo_guia', e.target.value)} style={selectStyle}>
                      <option value="REMITENTE">REMITENTE</option>
                      <option value="TRANSPORTISTA">TRANSPORTISTA</option>
                    </select>
                  </div>
                  <div className="form-row">
                    <label>Fecha de Guía *</label>
                    <input type="date" value={form.fecha_guia} onChange={e => setF('fecha_guia', e.target.value)} style={inputStyle} />
                  </div>
                  <AutoComplete
                    label="Proveedor / Cliente"
                    value={String(form.id_proveedor || '')}
                    options={entidades} idField="id_entidad"
                    displayField={e => `${e.ruc || ''} — ${e.razon_social}`}
                    onChange={v => {
                      const ent = entidades.find(x => String(x.id_entidad) === String(v));
                      setForm(f => ({ ...f, id_proveedor: v || '', ruc_proveedor: ent?.ruc || '', proveedor_nombre: ent?.razon_social || '' }));
                    }}
                    placeholder="Buscar por RUC o razón social..."
                  />
                </div>
              </div>

              {/* ── SECCIÓN 2: Referencias documentales ── */}
              <div className="rq-card">
                <div className="rq-card-title">Referencias Documentales</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0 14px', alignItems: 'start' }}>
                  <AutoComplete
                    label="Doc. Referencia 1"
                    value={String(form.id_tipo_doc_ref1 || '')}
                    options={tiposDoc} idField="id_tipo_documento" displayField="descripcion"
                    onChange={v => setF('id_tipo_doc_ref1', v)}
                    placeholder="Tipo documento..."
                  />
                  <div className="form-row">
                    <label>Nº Ref. 1</label>
                    <input value={form.num_doc_ref1} onChange={e => setF('num_doc_ref1', e.target.value)} placeholder="Número..." style={inputStyle} />
                  </div>
                  <AutoComplete
                    label="Doc. Referencia 2"
                    value={String(form.id_tipo_doc_ref2 || '')}
                    options={tiposDoc} idField="id_tipo_documento" displayField="descripcion"
                    onChange={v => setF('id_tipo_doc_ref2', v)}
                    placeholder="Tipo documento..."
                  />
                  <div className="form-row">
                    <label>Nº Ref. 2</label>
                    <input value={form.num_doc_ref2} onChange={e => setF('num_doc_ref2', e.target.value)} placeholder="Número..." style={inputStyle} />
                  </div>
                </div>
              </div>

              {/* ── SECCIÓN 3: Traslado ── */}
              <div className="rq-card">
                <div className="rq-card-title">Datos de Traslado</div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0 14px', alignItems: 'start' }}>
                  <AutoComplete
                    label="Lugar de Entrega (Almacén destino)"
                    value={String(form.id_almacen_destino || '')}
                    options={almacenesDestino} idField="id_almacen" displayField="display_destino"
                    onChange={v => setF('id_almacen_destino', v)}
                    placeholder="Buscar almacén destino..."
                    dropdownMinWidth={420}
                  />
                  <div className="form-row">
                    <label>Fecha de Traslado</label>
                    <input type="date" value={form.fecha_traslado} onChange={e => setF('fecha_traslado', e.target.value)} style={inputStyle} />
                  </div>
                  <div className="form-row">
                    <label>Fecha de Entrega</label>
                    <input type="date" value={form.fecha_entrega}
                      min={form.fecha_guia > form.fecha_traslado ? form.fecha_guia : form.fecha_traslado}
                      onChange={e => setF('fecha_entrega', e.target.value)} style={inputStyle} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '0 14px', alignItems: 'start', marginTop: 4 }}>
                  <AutoComplete
                    label="Solicitante"
                    value={String(form.id_solicitante || '')}
                    options={trabajadores} idField="id_trabajador" displayField="nombres"
                    onChange={v => setF('id_solicitante', v)}
                    placeholder="Buscar trabajador..."
                    twoLine codeField="dni"
                  />
                  <AutoComplete
                    label="Motivo de Traslado"
                    value={String(form.id_motivo_traslado || '')}
                    options={motivoTraslado} idField="id_motivo_traslado"
                    displayField={m => `${m.codigo} - ${m.descripcion}`}
                    onChange={v => setF('id_motivo_traslado', v)}
                    placeholder="Seleccionar motivo..."
                  />
                  <div className="form-row" style={{ display: 'flex', flexDirection: 'column' }}>
                    <label>Observaciones</label>
                    <textarea value={form.observacion} onChange={e => setF('observacion', e.target.value)}
                      rows={2} placeholder="Observaciones de la guía..."
                      style={{ flex: 1, width: '100%', boxSizing: 'border-box', padding: '6px 10px', fontSize: 12.5, borderRadius: 8, resize: 'vertical', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.11)', color: '#e2e8f0', fontFamily: 'Inter, sans-serif', outline: 'none', minHeight: 30 }}
                      onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)'; }}
                      onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.11)'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                </div>
              </div>

              {/* ── SECCIÓN 4: Ítems ── */}
              <div className="rq-card rq-card-table">
                <div className="rq-table-header">
                  <div className="rq-table-header-left">
                    <span className="rq-table-title">Artículos</span>
                    <button type="button" className="rq-btn-add-line" onClick={addLinea}>
                      + Agregar ítem
                    </button>
                  </div>
                  <span style={{ fontSize: 11, color: '#475569' }}>
                    {detalle.length} ítem{detalle.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="rq-table-container">
                  <table className="rq-articles">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>#</th>
                        <th style={{ width: 180 }}>Artículo</th>
                        <th>Descripción</th>
                        <th style={{ width: 72, textAlign: 'center' }}>U.M.</th>
                        <th style={{ width: 100, textAlign: 'right' }}>Cantidad</th>
                        <th style={{ width: 180 }}>Observación</th>
                        <th style={{ width: 36 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalle.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="rq-empty">
                            Sin artículos — use "+ Agregar ítem" para comenzar
                          </td>
                        </tr>
                      ) : detalle.map((linea, idx) => {
                        return (
                          <tr key={linea._key}>
                            <td className="rq-td-num">{idx + 1}</td>
                            <td className="rq-td-cod">
                              <AutoComplete
                                label="" value={String(linea.id_articulo || '')}
                                idField="id_articulo"
                                displayField={(a) => `[${a.codigo_interno || ''}] ${a.descripcion || ''}`}
                                selectedField="codigo_interno"
                                dropdownMinWidth={400}
                                asyncSearch={async (q) => { const r = await articuloService.search(q); return r.data || []; }}
                                minChars={3}
                                initialSelected={linea.id_articulo ? {
                                  id_articulo: linea.id_articulo,
                                  codigo_interno: linea.articulo_codigo,
                                  descripcion: linea.articulo_descripcion || linea.descripcion,
                                  id_unidad: linea.id_unidad,
                                  unidad_descripcion: linea.unidad_descripcion,
                                } : null}
                                onChange={(v, item) => onSelectArticulo(linea._key, v, item)}
                                placeholder="Buscar (mín. 3)..."
                              />
                            </td>
                            <td className="rq-td-desc">
                              <span className="rq-td-text">{linea.articulo_descripcion || linea.descripcion || ''}</span>
                            </td>
                            <td className="rq-td-um">
                              <span className="rq-td-text">{linea.unidad_descripcion || ''}</span>
                            </td>
                            <td className="rq-td-qty">
                              <input type="number" min="0.0001" step="any"
                                value={linea.cantidad}
                                onChange={e => updateLinea(linea._key, 'cantidad', e.target.value)} />
                            </td>
                            <td style={{ padding: '3px 6px' }}>
                              <input
                                type="text"
                                value={linea.observacion}
                                onChange={e => updateLinea(linea._key, 'observacion', e.target.value)}
                                placeholder="Observación..."
                                style={{ ...inputStyle, height: 26, fontSize: 11.5 }}
                              />
                            </td>
                            <td className="rq-td-del">
                              <button type="button" className="rq-btn-del"
                                onClick={() => removeLinea(linea._key)} title="Eliminar línea">
                                <TrashIcon />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>

          {/* ── Footer ── */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)',
            background: 'rgba(0,0,0,0.15)', flexShrink: 0,
          }}>
            <button className="btn btn-secondary" onClick={() => setView('list')} disabled={saving}>
              ← Volver a lista
            </button>
            <button className="btn btn-primary" onClick={handleSubmit}
              disabled={saving || loadingCat} style={{ minWidth: 160 }}>
              {saving ? 'Guardando...' : 'Generar Guía'}
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  /* ════════════════════════════════════════════════════════════════
     VISTA LISTA
  ════════════════════════════════════════════════════════════════ */
  const tableColumns = [
    {
      key: 'numero', label: 'Número', width: 120,
      render: r => (
        <span style={{ fontWeight: 700, color: '#60a5fa', fontSize: 12, fontFamily: 'monospace' }}>
          {fmtNumGR(r.serie_codigo, r.numero)}
        </span>
      ),
    },
    {
      key: 'tipo_guia', label: 'Tipo', width: 110,
      render: r => (
        <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{r.tipo_guia || '—'}</span>
      ),
    },
    {
      key: 'fecha_guia', label: 'Fecha', width: 90,
      render: r => <span style={{ fontSize: 11 }}>{(r.fecha_guia || '').slice(0, 10) || '—'}</span>,
    },
    { key: 'almacen_nombre', label: 'Almacén', style: { fontSize: 11 } },
    {
      key: 'almacen_destino_nombre', label: 'Destino', style: { fontSize: 11 },
      render: r => <span style={{ fontSize: 11 }}>{r.almacen_destino_nombre || '—'}</span>,
    },
    {
      key: 'proveedor_nombre', label: 'Proveedor / Cliente',
      render: r => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'nowrap' }}>
          {r.ruc_proveedor && <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', flexShrink: 0 }}>{r.ruc_proveedor}</span>}
          {r.ruc_proveedor && r.proveedor_nombre && <span style={{ color: '#374151', flexShrink: 0 }}>—</span>}
          <span style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.proveedor_nombre || '—'}</span>
        </div>
      ),
    },
    {
      key: 'estado', label: 'Estado', width: 100,
      render: r => {
        const cfg = ESTADO_GR_CFG[r.estado] || { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.2)' };
        return <Badge label={r.estado || '—'} cfg={cfg} />;
      },
    },
    {
      key: '_acciones', label: '', width: 96,
      render: r => (
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => handlePrint(r)}
            title="Imprimir guía"
            style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 6, cursor: 'pointer', color: '#22c55e', padding: '4px 6px', display: 'flex', alignItems: 'center' }}
          >
            <PrintIcon />
          </button>
          <button
            onClick={() => abrirEditar(r)}
            title={r.bloqueado ? 'No editable: tiene partes de entrada derivados' : 'Editar cabecera'}
            disabled={!!r.bloqueado}
            style={{
              background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.25)',
              borderRadius: 6, cursor: r.bloqueado ? 'not-allowed' : 'pointer', color: '#60a5fa',
              padding: '4px 6px', display: 'flex', alignItems: 'center',
              opacity: r.bloqueado ? 0.4 : 1,
            }}
          >
            <EditIcon />
          </button>
          <button
            onClick={() => handleDelete(r)}
            title={r.bloqueado ? 'No eliminable: tiene partes de entrada derivados' : 'Eliminar guía'}
            disabled={!!r.bloqueado}
            style={{
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 6, cursor: r.bloqueado ? 'not-allowed' : 'pointer', color: '#f87171',
              padding: '4px 6px', display: 'flex', alignItems: 'center',
              opacity: r.bloqueado ? 0.4 : 1,
            }}
          >
            <TrashIcon />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Layout>
      {alert    && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
      {toast    && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      {confirmDel && (
        <Confirm
          message={`¿Eliminar la guía ${fmtNumGR(confirmDel.serie_codigo, confirmDel.numero)}?`}
          onConfirm={confirmDelete}
          onCancel={() => setConfirmDel(null)}
        />
      )}

      {/* ── Modal edición cabecera ── */}
      {editModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#0f1b2d', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, width: '90%', maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div>
                <div style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 600 }}>Editar cabecera</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>{fmtNumGR(editModal.serie_codigo, editModal.numero)}</div>
              </div>
              <button onClick={() => setEditModal(null)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                <div className="form-row">
                  <label>Tipo de Guía *</label>
                  <select value={editForm.tipo_guia} onChange={e => setEF('tipo_guia', e.target.value)} style={selectStyle}>
                    <option value="REMITENTE">REMITENTE</option>
                    <option value="TRANSPORTISTA">TRANSPORTISTA</option>
                  </select>
                </div>
                <div className="form-row">
                  <label>Fecha de Guía *</label>
                  <input type="date" value={editForm.fecha_guia} onChange={e => setEF('fecha_guia', e.target.value)} style={inputStyle} />
                </div>
              </div>
              <AutoComplete
                label="Proveedor / Cliente"
                value={String(editForm.id_proveedor || '')}
                options={entidades} idField="id_entidad"
                displayField={e => `${e.ruc || ''} — ${e.razon_social}`}
                onChange={v => {
                  const ent = entidades.find(x => String(x.id_entidad) === String(v));
                  setEditForm(f => ({ ...f, id_proveedor: v || '', ruc_proveedor: ent?.ruc || '' }));
                }}
                placeholder="Buscar proveedor / cliente..."
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0 14px' }}>
                <AutoComplete
                  label="Doc. Ref. 1"
                  value={String(editForm.id_tipo_doc_ref1 || '')}
                  options={tiposDoc} idField="id_tipo_documento" displayField="descripcion"
                  onChange={v => setEF('id_tipo_doc_ref1', v)}
                  placeholder="Tipo..."
                />
                <div className="form-row">
                  <label>Nº Ref. 1</label>
                  <input value={editForm.num_doc_ref1} onChange={e => setEF('num_doc_ref1', e.target.value)} style={inputStyle} />
                </div>
                <AutoComplete
                  label="Doc. Ref. 2"
                  value={String(editForm.id_tipo_doc_ref2 || '')}
                  options={tiposDoc} idField="id_tipo_documento" displayField="descripcion"
                  onChange={v => setEF('id_tipo_doc_ref2', v)}
                  placeholder="Tipo..."
                />
                <div className="form-row">
                  <label>Nº Ref. 2</label>
                  <input value={editForm.num_doc_ref2} onChange={e => setEF('num_doc_ref2', e.target.value)} style={inputStyle} />
                </div>
              </div>
              <AutoComplete
                label="Almacén Destino"
                value={String(editForm.id_almacen_destino || '')}
                options={almacenes.map(a => ({ ...a, display_destino: `${a.nombre}${a.direccion ? ' | ' + a.direccion : ''}` }))}
                idField="id_almacen" displayField="display_destino"
                onChange={v => setEF('id_almacen_destino', v)}
                placeholder="Almacén destino..."
                dropdownMinWidth={360}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                <div className="form-row">
                  <label>Fecha de Traslado</label>
                  <input type="date" value={editForm.fecha_traslado} onChange={e => setEF('fecha_traslado', e.target.value)} style={inputStyle} />
                </div>
                <div className="form-row">
                  <label>Fecha de Entrega</label>
                  <input type="date" value={editForm.fecha_entrega} onChange={e => setEF('fecha_entrega', e.target.value)} style={inputStyle} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                <AutoComplete
                  label="Solicitante"
                  value={String(editForm.id_solicitante || '')}
                  options={trabajadores} idField="id_trabajador" displayField="nombres"
                  onChange={v => setEF('id_solicitante', v)}
                  placeholder="Buscar trabajador..."
                  twoLine codeField="dni"
                />
                <AutoComplete
                  label="Motivo de Traslado"
                  value={String(editForm.id_motivo_traslado || '')}
                  options={motivoTraslado} idField="id_motivo_traslado"
                  displayField={m => `${m.codigo} - ${m.descripcion}`}
                  onChange={v => setEF('id_motivo_traslado', v)}
                  placeholder="Seleccionar motivo..."
                />
              </div>
              <div className="form-row">
                <label>Observaciones</label>
                <textarea value={editForm.observacion} onChange={e => setEF('observacion', e.target.value)}
                  rows={2} placeholder="Observaciones..."
                  style={{ width: '100%', boxSizing: 'border-box', padding: '6px 10px', fontSize: 12.5, borderRadius: 8, resize: 'vertical', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.11)', color: '#e2e8f0', fontFamily: 'Inter, sans-serif', outline: 'none' }}
                  onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)'; }}
                  onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.11)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setEditModal(null)} disabled={savingEdit}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSaveEdit} disabled={savingEdit}>
                {savingEdit ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ padding: '0 0 16px' }}>
        <div className="oc-page-header">
          <h2>Guías de Remisión</h2>
          <div className="oc-header-actions">
            <button className="btn btn-secondary btn-sm" onClick={fetchRecords}>↺ Actualizar</button>
            <button className="btn btn-primary" onClick={irAFormulario}>+ Nueva Guía</button>
          </div>
        </div>

        {/* ── Barra de filtros ── */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr auto',
          gap: 10, marginBottom: 10, alignItems: 'end',
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 10, padding: '12px 16px',
        }}>
          <div>
            <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>Fecha desde</label>
            <input type="date" value={fFechaIni} onChange={e => setFFechaIni(e.target.value)} style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.09)'} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>Fecha hasta</label>
            <input type="date" value={fFechaFin} onChange={e => setFFechaFin(e.target.value)} style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.09)'} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>Número</label>
            <input value={fNumero} onChange={e => setFNumero(e.target.value)} placeholder="Buscar número..." style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.09)'} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>Estado</label>
            <select value={fEstado} onChange={e => setFEstado(e.target.value)} style={selectStyle}>
              <option value="">Todos</option>
              <option value="GENERADO">GENERADO</option>
              <option value="ANULADO">ANULADO</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>Tipo Guía</label>
            <select value={fTipo} onChange={e => setFTipo(e.target.value)} style={selectStyle}>
              <option value="">Todos</option>
              <option value="REMITENTE">REMITENTE</option>
              <option value="TRANSPORTISTA">TRANSPORTISTA</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => { setFFechaIni(weekAgo()); setFFechaFin(today()); setFNumero(''); setFEstado(''); setFTipo(''); }}
              style={{ height: 30, padding: '0 12px', fontSize: 11, fontWeight: 600, borderRadius: 7, border: '1px solid rgba(255,255,255,0.07)', cursor: 'pointer', background: 'rgba(255,255,255,0.03)', color: '#64748b', fontFamily: 'Inter, sans-serif' }}
            >
              ✕ Limpiar
            </button>
          </div>
        </div>

        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, paddingLeft: 2 }}>
          {recordsFiltrados.length} resultado{recordsFiltrados.length !== 1 ? 's' : ''} de {records.length} guías
        </div>

        <DataTable columns={tableColumns} data={recordsFiltrados} loading={loading} hideSearch hideToolbarRight />
      </div>
    </Layout>
  );
}
